import { CommonModule } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ScandataModel } from '../../../scandata/scandata.models';

import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { isNil } from '@trimble-gcs/common';
import { TranslationService } from '@trimble-gcs/i18n';
import { PluralArgs } from '@trimble-gcs/i18n-types';
import {
  ModusButtonModule,
  ModusIconModule,
  ModusSwitchModule,
  ModusTooltipModule,
} from '@trimble-gcs/modus';
import { ClassificationScheme } from '../../../classification/classification-scheme.model';
import { Classification } from '../../../classification/classification.model';
import { ClassificationService } from '../../../classification/classification.service';
import { ColorPickerComponent } from '../../../color-picker/color-picker.component';
import { ClassificationGraphComponent } from '../../../options-panel/selected-details/single-selected/classification/classification-graph/classification-graph.component';
import { PercentagePipe } from '../../../pipes/percentage.pipe';
import { Scan3dStyle } from '../../models/scan-3d-style';
import { Scan3dIntensityComponent } from '../scan-3d-intensity/scan-3d-intensity.component';
import { scan3dClassificationTranslations } from './scan-3d-classification.translations';

@UntilDestroy()
@Component({
  selector: 'sd-scan-3d-classification',
  imports: [
    CommonModule,
    ClassificationGraphComponent,
    ModusButtonModule,
    ModusIconModule,
    ModusTooltipModule,
    PercentagePipe,
    ReactiveFormsModule,
    ModusSwitchModule,
    ColorPickerComponent,
    Scan3dIntensityComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './scan-3d-classification.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Scan3dClassificationComponent {
  private readonly classificationService = inject(ClassificationService);
  private readonly translationService = inject(TranslationService);

  readonly scandataModel = input<ScandataModel>();
  readonly scan3dStyle = input.required<Scan3dStyle>();

  readonly styleChange = output<Partial<Scan3dStyle>>();

  readonly activeClassification = signal<Classification | null>(null);

  private readonly classificationSchemes = toSignal(
    this.classificationService.getClassificationSchemes(),
    {
      initialValue: [],
    },
  );

  readonly classifications = this.getClassifications();
  readonly hasClassifications = computed(() => this.classifications().length > 0);

  readonly unallocatedClassificationCount = computed(() => {
    return this.classificationService.getUnallocatedClassificationSchemes(
      this.classifications(),
      this.classificationSchemes(),
    ).length;
  });

  readonly unallocatedClassificationText = computed(() => {
    const count = this.unallocatedClassificationCount();
    const pluralArgs: PluralArgs = { count };
    const pluralMap = this.translations.unallocated;
    return this.translationService.translatePlural(pluralArgs, pluralMap);
  });

  readonly blendIntensityControl = new FormControl<boolean>(false);
  readonly showIntensitySettings = computed(() => this.scan3dStyle().blendIntensity);

  private colorPickerOpen = false;

  readonly translations = scan3dClassificationTranslations();

  constructor() {
    this.subscribeToScan3dStyle();
    this.subscribeToFormValueChanges();
  }

  changeColor(color: string, classification: Classification) {
    const scheme = classification.classificationScheme;
    scheme.rgba = color.replace('#', '').toUpperCase();

    const schemeChanges = this.getClassificationSchemes(scheme);
    this.styleChange.emit({ classificationSchemes: schemeChanges });
  }

  changeVisibility(classification: Classification) {
    const scheme = classification.classificationScheme;
    scheme.visible = !scheme.visible;

    const schemeChanges = this.getClassificationSchemes(scheme);
    this.styleChange.emit({ classificationSchemes: schemeChanges });
  }

  colorPickerToggle(open: boolean) {
    this.activeClassification.set(null);
    this.colorPickerOpen = open;
  }

  isActiveClassification(classification: Classification) {
    return classification === this.activeClassification();
  }

  setActiveClassification(classification: Classification | null = null) {
    if (this.colorPickerOpen) return;

    this.activeClassification.set(classification);
  }

  onStyleChange(style: Partial<Scan3dStyle>) {
    this.styleChange.emit(style);
  }

  private getClassificationSchemes(classificationScheme: ClassificationScheme) {
    return this.classifications().map((classification) =>
      classification.classificationScheme.id === classificationScheme.id
        ? classificationScheme
        : classification.classificationScheme,
    );
  }

  private getClassifications() {
    return computed(() => {
      const scandataModel = this.scandataModel();
      if (isNil(scandataModel)) return [];

      const scan3dStyle = this.scan3dStyle();
      const projectSchemes = this.classificationSchemes();

      return this.getScanClassifications(scandataModel, scan3dStyle, projectSchemes);
    });
  }

  private getScanClassifications(
    scandataModel: ScandataModel,
    scan3dStyle: Scan3dStyle,
    projectSchemes: ClassificationScheme[],
  ): Classification[] {
    const classifications = this.classificationService.getScanClassifications(
      scandataModel,
      projectSchemes,
    );

    const schemes = scan3dStyle.classificationSchemes;

    return classifications.map((classification) => {
      const classificationScheme =
        schemes.find((scheme) => scheme.id === classification.classificationScheme.id) ??
        classification.classificationScheme;

      return {
        ...classification,
        classificationScheme,
      };
    });
  }

  private subscribeToScan3dStyle() {
    toObservable(this.scan3dStyle)
      .pipe(untilDestroyed(this))
      .subscribe((style) =>
        this.blendIntensityControl.setValue(style.blendIntensity, { emitEvent: false }),
      );
  }

  private subscribeToFormValueChanges() {
    this.blendIntensityControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((blendIntensity) =>
        this.styleChange.emit({ blendIntensity: blendIntensity ?? false }),
      );
  }
}
